
import { scrapeArticle } from '@/lib/articleAnalysis.functions';
import { supabase } from '@/integrations/supabase/client';
import { NewsArticle } from '@/types/news';
import { cleanText, decodeEntities } from '@/lib/text';
import { INTERNAL_SOURCE_IDS } from '@/lib/internalSource';

// Transform database article to NewsArticle format
const transformArticle = (dbArticle: any): NewsArticle => ({
  id: dbArticle.id,
  slug: dbArticle.slug ?? null,
  title: cleanText(dbArticle.title),
  excerpt: cleanText(dbArticle.excerpt),
  content: decodeEntities(dbArticle.content),
  url: dbArticle.url,
  imageUrl: dbArticle.image_url,
  publishedAt: dbArticle.published_at,
  source: {
    id: dbArticle.source_id,
    name: cleanText(dbArticle.source_name),
    url: dbArticle.source_url,
    logoUrl: dbArticle.source_logo_url
  },
  author: dbArticle.author_name ? {
    name: cleanText(dbArticle.author_name),
    avatar: dbArticle.author_avatar
  } : undefined,
  category: cleanCategory(dbArticle.category || 'General'),
  readTime: dbArticle.read_time || '5 min read',
  likes_count: dbArticle.likes_count || 0,
  comments_count: dbArticle.comments_count || 0,
  audio_url: dbArticle.audio_url,
  contentCleaned: dbArticle.content_cleaned ? decodeEntities(dbArticle.content_cleaned) : undefined,
  topicTags: Array.isArray(dbArticle.topic_tags) ? dbArticle.topic_tags : undefined
});


// Comprehensive CDATA cleaning function
export const cleanCategory = (category: string): string => {
  if (!category) return 'Technology';
  
  let cleanedCategory = category;
  
  // Remove all variations of CDATA tags comprehensively
  cleanedCategory = cleanedCategory
    .replace(/<!\[CDATA\[(.*?)\]\]>/gs, '$1')
    .replace(/&lt;!\[CDATA\[(.*?)\]\]&gt;/gs, '$1')
    .replace(/\[CDATA\[(.*?)\]\]/gs, '$1')
    .replace(/&lt;!\[CDATA\[/g, '')
    .replace(/\]\]&gt;/g, '')
    .replace(/<!\[CDATA\[/g, '')
    .replace(/\]\]>/g, '')
    .replace(/<!CDATA\[/g, '')
    .replace(/CDATA\[/g, '')
    .replace(/\]\]/g, '')
    .replace(/[\[\]]/g, '') // Remove any remaining brackets
    .trim();
  
  // Remove any remaining HTML tags
  cleanedCategory = cleanedCategory.replace(/<[^>]*>/g, '').trim();
  
  // Normalize common categories
  const categoryLower = cleanedCategory.toLowerCase();
  if (categoryLower.includes('bitcoin') || categoryLower.includes('btc')) {
    return 'Bitcoin';
  } else if (categoryLower.includes('ethereum') || categoryLower.includes('eth')) {
    return 'Ethereum';
  } else if (categoryLower.includes('defi') || categoryLower.includes('dao')) {
    return 'DeFi';
  } else if (categoryLower.includes('nft')) {
    return 'NFTs';
  } else if (categoryLower.includes('regulation') || categoryLower.includes('legal')) {
    return 'Regulation';
  } else if (categoryLower.includes('altcoin') || categoryLower.includes('alt coin')) {
    return 'Altcoins';
  } else if (categoryLower.includes('web3') || categoryLower.includes('web 3')) {
    return 'Web3';
  } else if (categoryLower.includes('security') || categoryLower.includes('hack')) {
    return 'Security';
  } else if (categoryLower.includes('exchange') || categoryLower.includes('trading')) {
    return 'Exchanges';
  } else if (categoryLower.includes('market')) {
    return 'Markets';
  } else if (cleanedCategory.length === 0) {
    return 'Technology';
  }
  
  // Capitalize first letter
  return cleanedCategory.charAt(0).toUpperCase() + cleanedCategory.slice(1);
};

// Enhanced URL normalization to prevent duplicates
export const normalizeUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const paramsToRemove = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'gclid', 'ref', '_ga', '_gl'];
    paramsToRemove.forEach(param => urlObj.searchParams.delete(param));
    
    let normalized = urlObj.origin + urlObj.pathname;
    if (urlObj.search && urlObj.searchParams.toString()) {
      normalized += '?' + urlObj.searchParams.toString();
    }
    
    return normalized.replace(/\/$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().replace(/\/$/, '');
  }
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Background re-scrape is attempted at most once per article per session.
const reScrapeAttempted = new Set<string>();

// Enhanced article fetch with improved auto-improvement.
// Accepts either the readable slug or the legacy UUID.
export const fetchArticleWithFallback = async (identifier: string): Promise<NewsArticle | null> => {
  try {
    let { data: dbArticle, error } = await supabase
      .from('articles')
      .select('*')
      .eq(UUID_RE.test(identifier) ? 'id' : 'slug', identifier)
      .maybeSingle();

    // Legacy slug: look it up in the slug history and load the current article.
    if (!dbArticle && !UUID_RE.test(identifier)) {
      const { data: historic } = await supabase
        .from('article_slug_history')
        .select('article_id')
        .eq('old_slug', identifier)
        .maybeSingle();

      if (historic?.article_id) {
        const retry = await supabase
          .from('articles')
          .select('*')
          .eq('id', historic.article_id)
          .maybeSingle();
        dbArticle = retry.data;
        error = retry.error;
      }
    }

    if (!error && dbArticle) {

      const articleId = dbArticle.id;
      // Check if article needs improvement (optimized threshold)
      const contentLength = dbArticle.content?.length || 0;
      const hasPlaceholder = dbArticle.content?.includes('Content will be loaded shortly') || false;
      const hasReadMoreText = dbArticle.content?.toLowerCase().includes('continue reading') || 
                             dbArticle.content?.toLowerCase().includes('read more') || false;
      
      // Only re-scrape if content is truly inadequate (1200 chars is minimum for quality crypto articles)
      if ((contentLength < 1200 && (hasPlaceholder || hasReadMoreText)) || contentLength < 300) {
        // Guard against undefined values and fire-and-forget
        if (articleId && dbArticle.url && dbArticle.source_id && dbArticle.source_name && !reScrapeAttempted.has(articleId)) {
          reScrapeAttempted.add(articleId);
          reScrapeArticle(dbArticle.url, dbArticle.source_id, dbArticle.source_name, articleId)
            .catch(error => console.error('Background article improvement failed:', error));
        }
      }
      
      return transformArticle(dbArticle);
    }

    return null;
    
  } catch (error) {
    console.error('❌ Error in fetchArticleWithFallback:', error);
    return null;
  }
};

// Enhanced re-scraping function with better error handling
export const reScrapeArticle = async (url: string, sourceId: string, sourceName: string, articleId: string): Promise<void> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return; // full-text fetching needs a signed-in reader

    const data = await scrapeArticle({
      data: { articleId, sourceName, force: true },
    });

    if (data?.success) {
      console.log(`✅ Article re-scraped with FULL content successfully`);
    } else {
      console.error('❌ Re-scraping returned failure:', data);
    }
    
  } catch (error) {
    console.error('❌ Error in reScrapeArticle:', error);
  }
};

// Enhanced scrape and store with better error handling
export const scrapeAndStoreArticle = async (url: string, sourceId: string, sourceName: string): Promise<NewsArticle | null> => {
  try {
    const normalizedUrl = normalizeUrl(url);
    
    console.log(`🌐 Triggering enhanced scrape for: ${url}`);
    
    // Full-text extraction runs against a stored article row, so an unsaved
    // URL cannot be scraped here.
    console.warn('Cannot scrape an article that is not stored yet', normalizedUrl, sourceId, sourceName);
    throw new Error('Failed to scrape article with enhanced extraction');
    
  } catch (error) {
    console.error('❌ Error in enhanced scrapeAndStoreArticle:', error);
    return null;
  }
};

// ---------------------------------------------------------------------------
// Feed list query (lean): selects ONLY the columns the article card renders.
// The article body (`content`) is intentionally excluded, it is fetched on the
// article page by id. Keyset pagination on published_at (no OFFSET, no COUNT).
// ---------------------------------------------------------------------------
const sel = (s: string): string => s;

const FEED_COLUMNS = sel(
  'id, title, excerpt, url, image_url, published_at, source_id, source_name, source_url, source_logo_url, author_name, author_avatar, category, read_time, likes_count, comments_count, audio_url'
);

export interface ArticlePage {
  articles: NewsArticle[];
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Sources switched off in Admin → News Sources must not surface any articles.
// Anonymous visitors can only read active sources, so we whitelist their ids.
// ---------------------------------------------------------------------------
let activeSourceCache: { ids: string[]; at: number } | null = null;
const ACTIVE_TTL_MS = 60_000;

export const getActiveSourceIds = async (): Promise<string[]> => {
  if (activeSourceCache && Date.now() - activeSourceCache.at < ACTIVE_TTL_MS) {
    return activeSourceCache.ids;
  }
  try {
    const { data, error } = await (supabase as any)
      .from('news_sources')
      .select('id')
      .eq('is_active', true);
    if (error) throw error;
    // Our own stories are not registered as an outside news source, but they belong in the feed.
    const ids = [
      ...((data || []) as { id: string }[]).map((r) => r.id).filter(Boolean),
      ...INTERNAL_SOURCE_IDS,
    ];
    activeSourceCache = { ids, at: Date.now() };
    return ids;
  } catch (e) {
    console.error('⚠️ Could not load active news sources:', e);
    return activeSourceCache?.ids ?? [];
  }
};

export const clearActiveSourceCache = () => {
  activeSourceCache = null;
};

// Topic tag counts across the WHOLE published archive (not just loaded pages).
export const getTopicTagCounts = async (): Promise<Record<string, number>> => {
  const { data, error } = await (supabase as any).rpc('get_topic_tag_counts');
  if (error) {
    console.error('⚠️ Could not load topic tag counts:', error);
    return {};
  }
  const out: Record<string, number> = {};
  for (const row of (data || []) as { tag: string; count: number }[]) {
    out[row.tag] = Number(row.count) || 0;
  }
  return out;
};

export const getArticlesPage = async (params: {
  search?: string;
  cursor?: string | null;
  limit?: number;
  tags?: string[];
  /** Translation code ('no', 'zh-Hant'): only fully translated stories are listed. */
  translatedIn?: string | null;
}): Promise<ArticlePage> => {
  const limit = params.limit ?? 24;

  try {
    const activeIds = await getActiveSourceIds();

    let query = supabase
      .from('articles')
      .select(FEED_COLUMNS)
      .eq('is_published', true)
      .order('published_at', { ascending: false })
      .limit(limit);

    if (activeIds.length > 0) {
      query = query.in('source_id', activeIds);
    }

    if (params.translatedIn) {
      query = (query as any).contains('translated_locales', [params.translatedIn]);
    }


    if (params.search) {
      const term = params.search.replace(/[%,]/g, ' ');
      query = query.or(`title.ilike.%${term}%,excerpt.ilike.%${term}%`);
    }

    if (params.tags && params.tags.length > 0) {
      query = (query as any).overlaps('topic_tags', params.tags);
    }

    if (params.cursor) {
      query = query.lt('published_at', params.cursor);
    }

    const { data, error } = await query.returns<Record<string, unknown>[]>();
    if (error) throw error;

    const rows = data || [];
    const articles = rows.map(transformArticle);
    const last = rows[rows.length - 1] as { published_at?: string } | undefined;

    return {
      articles,
      nextCursor: rows.length === limit && last?.published_at ? last.published_at : null,
    };

  } catch (error) {
    console.error('❌ Error in getArticlesPage:', error);
    return { articles: [], nextCursor: null };
  }
};

// Enhanced article retrieval with better filtering

export const getArticlesFromDatabase = async (filters?: {
  search?: string;
  category?: string;
  sourceId?: string;
  limit?: number;
  offset?: number;
}): Promise<{ articles: NewsArticle[]; total: number }> => {
  try {
    console.log('📡 Fetching articles from enhanced database...');

    const activeIds = await getActiveSourceIds();

    let query = supabase
      .from('articles')
      .select('*', { count: 'exact' })
      .eq('is_published', true) // Only show published articles to regular users
      .order('published_at', { ascending: false })
      .order('created_at', { ascending: false });

    if (activeIds.length > 0) {
      query = query.in('source_id', activeIds);
    }


    if (filters?.search) {
      query = query.or(`title.ilike.%${filters.search}%,excerpt.ilike.%${filters.search}%,content.ilike.%${filters.search}%`);
    }

    if (filters?.category) {
      const categoryLower = filters.category.toLowerCase();
      if (filters.category.includes(',')) {
        const categories = filters.category.split(',');
        const categoryFilters = categories.map(cat => `category.ilike.%${cat.trim()}%`).join(',');
        query = query.or(categoryFilters);
      } else {
        query = query.ilike('category', `%${categoryLower}%`);
      }
    }

    if (filters?.sourceId) {
      query = query.eq('source_id', filters.sourceId);
    }

    // Show articles from the last 365 days for better coverage
    const oneYearAgo = new Date();
    oneYearAgo.setDate(oneYearAgo.getDate() - 365);
    query = query.gte('published_at', oneYearAgo.toISOString());

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 20) - 1);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error('❌ Error fetching articles from database:', error);
      throw error;
    }

    // Transform and deduplicate articles based on normalized URLs
    const articles = (data || []).map(transformArticle);
    const uniqueArticles = articles.filter((article, index, self) => {
      const normalizedUrl = normalizeUrl(article.url);
      return index === self.findIndex(a => normalizeUrl(a.url) === normalizedUrl);
    });
    
    console.log(`📊 Retrieved ${uniqueArticles.length} unique articles from enhanced database`);
    
    return {
      articles: uniqueArticles,
      total: count || 0
    };

  } catch (error) {
    console.error('❌ Error in getArticlesFromDatabase:', error);
    return { articles: [], total: 0 };
  }
};

